/**
 * What a held card shows while it peeks (ADR 0193, decision 6): its first
 * three activities in time order, how many more the popup has, and the facts
 * the owner kept in the prototype. Pure functions of the day view's model, so
 * the cards only lay them out.
 *
 * The facts, in this order, each only when there's something to say:
 * - **Caller** or **Contact**: the people its calls and email were with.
 * - **Subject**: its emails' subjects.
 * - **Draft notes**: what the tech typed into its Log form, if anything.
 * - **Continues**: the open ticket a Ready block continues.
 * - **Ticket**: a logged block's ticket's status and age.
 *
 * A HIPAA client's caller and subject are never shown (section 14.4): they
 * say "Not shown: a HIPAA client" and "Withheld: a HIPAA client". Its
 * activities' titles are their summaries, as the popup shows them, which the
 * server keeps to which way a call or email went. Billing and the ticket's
 * other time entries aren't shown.
 *
 * What it reads of an activity's details is the API's own, as the server
 * writes it: a call's `number` and `contact_name` (`services/calls.py`; no
 * `contact_name` for a HIPAA client's), and an email's `subject`,
 * `subject_withheld`, `from`, `to`, `cc` and `bcc`, each address
 * `{address, name}` (`domain/email_activity.py`), its other party in its
 * external key, `email:<address>` (ADR 0128).
 */
import type { Activity } from "../../api/types";
import { formatClockRange, formatShortDay, todayIn } from "../../lib/dates";
import { emailFromGroupKey } from "../../lib/emailDomains";
import { formatPhone } from "../../lib/phones";
import { type BlockView, openTicketToAdd } from "./model";
import { stateText, ticketLine, ticketName } from "./tickets";

/** How many activities a peek lists; the popup has the rest. */
export const PEEK_ACTIVITIES = 3;

/** A HIPAA client's caller and subject (ADR 0193, decision 6). */
export const CALLER_WITHHELD = "Not shown: a HIPAA client";
export const SUBJECT_WITHHELD = "Withheld: a HIPAA client";

export interface PeekFact {
  label: "Caller" | "Contact" | "Subject" | "Draft notes" | "Continues" | "Ticket";
  value: string;
  /** Not shown, by HIPAA's rule: it reads muted. */
  withheld?: boolean;
  /** Free text that can run long: three lines at most. */
  clamp?: boolean;
}

function byStart(a: Activity, b: Activity): number {
  return Date.parse(a.started_at) - Date.parse(b.started_at) || a.id.localeCompare(b.id);
}

/** The blocks' activities in time order: the first three, and how many more. */
export function peekActivities(blocks: readonly BlockView[]): { shown: Activity[]; more: number } {
  const all = blocks.flatMap((b) => b.activities).sort(byStart);
  return { shown: all.slice(0, PEEK_ACTIVITIES), more: Math.max(0, all.length - PEEK_ACTIVITIES) };
}

/**
 * An activity's times, as the popup shows them: "9:12–9:15", with its day
 * first when it's an email that moved in from an earlier day (ADR 0131).
 */
export function peekRange(activity: Activity, block: Pick<BlockView, "startedAt">, timeZone: string): string {
  const clock = formatClockRange(activity.started_at, activity.ended_at, timeZone);
  const day = todayIn(timeZone, new Date(activity.started_at));
  return day === todayIn(timeZone, new Date(block.startedAt)) ? clock : `${formatShortDay(day)} ${clock}`;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

/** "Re: Re: Printer offline" is "Printer offline": a reply's subject is its thread's. */
function threadSubject(subject: string): string {
  return subject.replace(/^(?:(?:re|fwd?|fw)\s*:\s*)+/i, "").trim();
}

function unique(values: readonly (string | null)[]): string[] {
  const seen: string[] = [];
  for (const value of values) {
    if (value !== null && !seen.some((s) => s.toLowerCase() === value.toLowerCase())) {
      seen.push(value);
    }
  }
  return seen;
}

/** An email's other party: their address, and their name as the email gave it. */
function emailParty(activity: Activity): { address: string; name: string | null } | null {
  const address = emailFromGroupKey(activity.external_key);
  if (address === null) {
    return null;
  }
  const { from, to, cc, bcc } = activity.details;
  const people = [from, ...[to, cc, bcc].flatMap((list) => (Array.isArray(list) ? (list as unknown[]) : []))];
  for (const person of people) {
    if (typeof person === "object" && person !== null) {
      const { address: at, name } = person as { address?: unknown; name?: unknown };
      if (typeof at === "string" && at.trim().toLowerCase() === address.toLowerCase()) {
        return { address, name: text(name) };
      }
    }
  }
  return { address, name: null };
}

/** Who its calls and email were with; a HIPAA client's never. */
function callerFact(activities: readonly Activity[], hipaa: boolean): PeekFact | null {
  const calls = activities.filter((a) => a.source === "phone");
  const emails = activities.filter((a) => a.source === "email");
  if (calls.length === 0 && emails.length === 0) {
    return null;
  }
  if (hipaa) {
    return { label: "Caller", value: CALLER_WITHHELD, withheld: true };
  }
  const callers = calls.map((a) => text(a.details.contact_name));
  const parties = emails.map(emailParty);
  const names = unique([...callers, ...parties.map((p) => p?.name ?? null)]);
  if (names.length > 0) {
    return { label: callers.some((c) => c !== null) ? "Caller" : "Contact", value: names.join(", ") };
  }
  const [number] = unique(calls.map((a) => text(a.details.number))).map(formatPhone);
  if (number !== undefined) {
    return { label: "Caller", value: `${number}, not a known contact` };
  }
  const addresses = unique(parties.map((p) => p?.address ?? null));
  return addresses.length > 0 ? { label: "Contact", value: addresses.join(", ") } : null;
}

/** Its emails' subjects: two, then how many more; a HIPAA client's never. */
function subjectFact(activities: readonly Activity[], hipaa: boolean): PeekFact | null {
  const emails = activities.filter((a) => a.source === "email");
  if (emails.length === 0) {
    return null;
  }
  // A subject the server withheld had a HIPAA client on its email (ADR 0122).
  const kept = emails.filter((a) => a.details.subject_withheld !== true);
  if (hipaa || kept.length === 0) {
    return { label: "Subject", value: SUBJECT_WITHHELD, withheld: true };
  }
  const subjects = unique(kept.map((a) => text(a.details.subject)).map((s) => (s === null ? null : threadSubject(s) || null)));
  if (subjects.length === 0) {
    return null;
  }
  const more = subjects.length > 2 ? ` · +${String(subjects.length - 2)} more` : "";
  return { label: "Subject", value: subjects.slice(0, 2).join(" · ") + more };
}

/**
 * The facts a peek shows for a card of `blocks` (a block's, or a ticket's,
 * ADR 0189): its first block decides whether it continues a ticket, and its
 * ticket. `draftNotes`: what the tech typed as its notes, if anything.
 */
export function peekFacts(
  blocks: readonly BlockView[],
  { draftNotes, now, timeZone }: { draftNotes: string | null; now: Date; timeZone: string },
): PeekFact[] {
  const [first] = blocks;
  if (first === undefined) {
    return [];
  }
  const activities = blocks.flatMap((b) => b.activities).sort(byStart);
  const hipaa = blocks.some((b) => b.isHipaa);
  const facts: (PeekFact | null)[] = [callerFact(activities, hipaa), subjectFact(activities, hipaa)];
  const notes = text(draftNotes);
  if (notes !== null) {
    facts.push({ label: "Draft notes", value: `“${notes}”`, clamp: true });
  }
  const continues = openTicketToAdd(first);
  if (continues !== null) {
    facts.push({ label: "Continues", value: `Ticket ${ticketName(continues)}, still open` });
  }
  if (first.kind === "logged" && first.ticket !== null) {
    const line = ticketLine(first.ticket, now, timeZone) ?? stateText(first.ticket, now, timeZone).text;
    if (line !== null) {
      facts.push({ label: "Ticket", value: line });
    }
  }
  return facts.filter((f): f is PeekFact => f !== null);
}
